import { ITableReport } from "./TableInterfaces";
import { DropdownItem } from "src/Interfaces/dropdownInterfaces";
import { OrderBook, OrderStatus } from "./orderInterfaces";
import { StepInstrumentType, StepOrderSide } from "./stepInterfaces";
import { Packet } from "./commonDtos";

export interface IAutomaticTaskOperationResult extends Packet<boolean> {
  succeededTaskIds?: number[];
  errors?: { taskId: number; optionName: string; message: string }[];
}

export interface IBubbleConfig {
  upperboundfactor: number;
  lowerboundfactor: number;
  maxBubbleDiffrencePercent: number;
  minBubbleDiffrencePercent: number;
}

export interface IDeltaConfig {
  upperboundfactor: number;
  lowerboundfactor: number;
  maxDeltaPercentage: number;
  minDeltaPercentage: number;
}

export interface IRemainingDayConfig {
  startRemaingDay: number;
  endRemainingDay: number;
  oneDayStepPercentage: number;
}

export interface ISellOrBuyQueueConfig {
  upperboundfactor: number;
  lowerboundfactor: number;
  minAmount: number;
  maxAmount: number;
}

export interface ISearchParameters {}

export interface IBaseHedgerPairTableReport extends ITableReport {
  baseInsId: string;
  hedgeInsId: string;
  refrenceRemainingType: 1 | 2;
  refrecnceRemainingManualValue: string;
  hedgerRefrenceDueDateType: 1 | 2;
  refrenceDueDate: string;
  hedgerRefrenceDueDateStr: string;
  hedgerLongTermsTurbulence: string;
  v_100_Base_Percent: number;
  v_100_Hedger_Percent: number;
  iV_inf_turbulence: number;
  baseToHedgerPerformance: number;
  baseToHedgerRate: number;
  sentiment: number;
  maxIV_30_Base: number;
  minIV_30_Base: number;
  accounts: DropdownItem[];
  longtermHedgerVolatility: number;
}

export interface IBaseHedgerPairAddOrEdit {
  id?: number;
  baseInsId: string;
  hedgeInsId: string;
  refrenceRemainingType: 1 | 2;
  refrecnceRemainingManualValue: string;
  refrenceDueDate: string;
  v_100_Base_Percent: number;
  v_100_Hedger_Percent: number;
  iV_inf_turbulence: number;
  baseToHedgerPerformance: number;
  baseToHedgerRate: number;
  sentiment: number;
  maxIV_30_Base: number;
  minIV_30_Base: number;
  accountIds: (string | number)[];
  longtermHedgerVolatility: number;
}

export enum RefrenceRemainingType {
  Manual = 1,
  Auto = 2,
}

export interface ICalculationTableReport extends ITableReport {}

export interface IIVOptionSearchParams {
  baseInsId: string | number;
  toDate: string | number;
  accountId: string | number;
}

export interface IVConfigSettingDto {
  minHedgerOptionVolume: string | number;
}

export interface IHedgerDuedateTableReport extends ITableReport {}

export interface IHedgerAccountDuedateTableReport extends ITableReport {}

export interface IExeConfigDto {
  baseInsTolerance: IBaseInsTolerance[];
  decreaseIVUnit: IIVUnit;
  increaseIVUnit: IIVUnit;
  putDecreaseIVUnit: IIVUnit;
  putIncreaseIVUnit: IIVUnit;
  riskFreeRate?: number;
  callRiskFreeRate: number;
  putRiskFreeRate: number;
  ivExpirationTime: number;
  involvedFundToPortfovalueMinRatio: number;
  maxFairpriceChangePercentage: number;
  removeExtraOrders: boolean;
  minOptionPower: number;
}

export interface IBaseInsTolerance {
  baseName: string;
  baseInsId: string;
  baseInsName: string;
  deltaTolerance: number;
  ivStepOneTradeVolume: number;
  ivStepTwoTradeVolume: number;
  targetDelta: number;
  isCallRequired: boolean;
  callBasePriceAdjustmentFactor: number;
  putBasePriceAdjustmentFactor: number;
}

export interface IIVUnit {
  highDeltaStepOne: string;
  highDeltaStepTwo: string;
  lowDeltaStepOne: string;
  lowDeltaStepTwo: string;
  neturalDeltaStepOne: string;
  neturalDeltaStepTwo: string;
}

export interface IOpenOrder {
  side: StepOrderSide;
  orderSide?: StepOrderSide;
  price: number;
  volume: number;
  count?: number;
  row?: number;
}

export interface IOrderbookPairResp {
  fullOrderBook: OrderBook;
  baseInsPriceLive: number;
  baseInsPriceSnapshot: number;
  liveToSnapshotChangeRatio: number;
  liveFairPrice: number;
  liveIV: number;
  snapshotIV: number;
  ivRange: number;
  netVolume: number;
  basePriceTolerance: number;
  volatilityTolerancePercent: number;
  taskStatus: number;
  correspondingRef: number;
  lastIVChangeByVolumeStr: string;
  openOrders: IOpenOrder[];
  monthlyGainCoveredCall: number;
  monthlyGainCoveredCallRef: number;
  leg1AtmHedger: string;
  leg2AtmHedger: string;
  leg1Iv: string;
  leg2Iv: string;
}

export type IOrderBookDepth = Pick<IOrderbookPairResp, "fullOrderBook" | "openOrders">;
export interface IOrderBookDetails {
  baseInsPriceLive: number;
  liveFairPrice: number | null;
  taskStatus: string | null;
  correspondingRef: string | null;
  lastIVChangeByVolumeStr: string | null;
  monthlyGainCoveredCall: number | null;
  monthlyGainCoveredCallRef: number | null;
  leg1AtmHedger: string | null;
  leg2AtmHedger: string | null;
  leg1Iv: number | null;
  leg2Iv: number | null;
}

export interface ITaskTableReport extends ITableReport {
  username: string;
  userId: number;
  applicationUsername: string;
  snapshot_FairPrice: number;
  snapshot_BasePrice: number;
  lastSnapshotTime: string;
  lastSnapshotStr: string;
  isActive: boolean;
  optionName: string;
  optionId: number;
  baseInsFactor: number;
  insId: number;
  accountId: string;
  basePriceTolerance: number;
  basePriceAdjustmentFactor: number;
  volatility: number;
  iVTolerancePercent: number;
  volatilityTolerancePercent: number;
  ivModeStr: "اتوماتیک" | "دستی" | "پیوسته";
  ivManualValue: number;
  orderType: "Open" | "Offset";
  continuousIV: number;
  minimumTriggerIntervalSeconds: number | null;
  usedBuyingPower: number;
}

export interface ITaskSearchParam extends ISearchParameters {
  baseInsId: string;
}

export interface IHedgerDuedateChartsReport {
  graphPoints: Array<Graph_Point_Dto>;
  name: string;
}

export interface Graph_Point_Dto {
  fromTime: string;
  toTime: string;
  isAvailable: boolean;
  price: number | null;
  becomeAvailable: boolean;
  comment: string;
}

export interface IExecusionSearchParams {
  id?: number;
  insId: number;
  accountId: string;
  basePriceTolerance: number;
  volatilityTolerance: number;
  basePriceAdjustmentFactor: number;
  ivtype: "Manual" | "Auto" | "Continues";
  ivManualValue: number | null;
  orderType: "Open" | "Offset";
  continuousIV: number;
  minimumTriggerIntervalSeconds: number;
}

export enum IVType {
  Manual = "Manual",
  Auto = "Auto",
  Continues = "Continues",
}

export enum OrderType {
  Open = "Open",
  Offset = "Offset",
}
interface CommentItem {
  label: string;
  value: string;
}

interface Point {
  x: number;
  y: number;
  commentItems: CommentItem[];
}

interface Series {
  serieName: string;
  points: Point[];
}

export interface ChartData {
  titleX: string;
  titleY: string;
  series: Series[];
}

export interface ITriggerTableReport extends ITableReport {}

export interface IPreviewOffer {
  rowNo?: number;
  disabledByBuyingPower?: boolean;
  account: string;
  calculatedPrice: number;
  calculatedVolume: number;
  finalVolume: number;
  requiredBuyingPower: number;
}

export interface IGetPreviewOfferResp {
  buy: IPreviewOffer[];
  sell: IPreviewOffer[];
  taskId: number;
  account: string;
  portfoAsset: string;
  orderType: string;
  totalRequiredBuyingPower: number;
  iv: number | null;
  basePrice: number | null;
  equilibriumPrice: number;
  isBuyingPowerLimited?: boolean;
  minimumBuyingPower?: number | null;
  availableBuyingPower?: number | null;
  isAccountConnected?: boolean;
  blockingReason?: string | null;
}

export interface IOrderTableReport extends ITableReport {
  orderSide: StepOrderSide;
  orderSideClassname: string;
  sahraName: string;
  operator: string;
  namad: string;
  optionType: string;
  totalSize: number;
  firstStep: number;
  lastStep: number;
  stepCount: number;
  doneStepCount: number;
  filledVolume: number;
  errorCount: number;
  date: string;
  instrumentType: StepInstrumentType;
  instrument: string;
  baseInstrument: string;
  optionInstrument: string;
  orderStatus: OrderStatus;
  isActive: true;
  userId: number;
}

export interface IOrderTableSearchParams extends ISearchParameters {
  taskId?: number;
}

export interface ITradeTableSearchParams extends ISearchParameters {
  taskId?: number;
  accountId?: number;
  optionInstrumentId?: string;
}

export interface ITradeTableReport extends ITableReport {}

export interface IMarketmakingTaskSearchParam extends ISearchParameters {
  baseInsId: string;
  justActive: boolean;
}

export interface ITaskExcelPreview {
  headers: string[];
  fields?: string[];
  rows: { row: number; taskId: number | null; values: string[]; changedFields?: string[]; willChange: boolean; error: string | null }[];
}

export interface ITaskExcelResult {
  updatedCount: number;
  unchangedCount: number;
  errors: { row: number; taskId: number | null; message: string }[];
}

export interface IIVPutSearchParams {
  baseInsId: string | number;
  toDate: string | number;
  accountId: string | number;
}

export interface IIVTablePutReport extends ITableReport {
  putOptionCode: string;
  putOptionName: string;
}

export interface IConversionConfigDto {
  baseConversionItems: IBaseConversionItems[];
  minOrderbookVolume: number;
}

export interface IBaseConversionItems {
  baseInsId: string;
  baseInsName?: string;
  conversionProfitType: IConversionProfitType;
  conversionRef: string;
  manualConversionProfit: number;
}

export enum IConversionProfitType {
  Auto = 1,
  SemiAuto = 2,
  Manual = 3,
}

export interface IVChartData {
  name: string;
  graphPoints: Array<Graph_Point_Dto>;
}

export interface IChartCommonSearchParam {
  taskId: number;
}
